import React, { useEffect, useRef, useState } from 'react';
import { Copy, EyeOff, KeyRound, ShieldAlert, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { inventoryApi, errorText } from '../../lib/inventoryApi';
import { LinkPicker, Chip } from './InventoryTable';
import { fieldLabel } from '../../lib/inventoryTable';

/*
 * Inventory dialogs (spec 01.4 section 6): the password reveal behind a click AND a confirm (every reveal is logged
 * in History by the server), the admin's credential editor, CSV import with a dry run first, and the generic
 * "add a row" form built from the server's field metadata.
 */

const LABELS = {
  password: 'Password', totp_secret: '2FA secret', backup_codes: 'Backup codes', recovery_email: 'Recovery email',
  recovery_email_password: 'Recovery email password', recovery_phone: 'Recovery phone', username: 'Username', notes: 'Notes',
};
export const HIDE_AFTER_MS = 30000;

/**
 * kind 'account' | 'network'. Step 1: what is stored (flags only, no values). Step 2: "This is logged in History.
 * Reveal now?". Step 3: the values with copy buttons, hidden again after 30 s.
 */
export function RevealDialog({ open, onOpenChange, kind, row, isAdmin, keyConfigured, fields }) {
  const [step, setStep] = useState('flags');
  const [flags, setFlags] = useState(null);
  const [values, setValues] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({});
  const timer = useRef(null);

  useEffect(() => {
    if (!open || !row) return undefined;
    setStep('flags'); setValues(null); setError(null); setEdit({});
    if (kind === 'account') {
      inventoryApi.credentialFlags(row.id).then((r) => setFlags(r.data)).catch((e) => setError(errorText(e)));
    } else {
      setFlags({ stored: !!row.hasSecret, canReveal: !!row.links?.canReveal, keyConfigured });
    }
    return () => clearTimeout(timer.current);
  }, [open, row, kind, keyConfigured]);

  const reveal = async () => {
    setBusy(true); setError(null);
    try {
      const r = kind === 'account' ? await inventoryApi.revealCredential(row.id) : await inventoryApi.revealNetworkSecret(row.id);
      setValues(r.data.values || {});
      setStep('shown');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { setValues(null); setStep('flags'); }, HIDE_AFTER_MS);
    } catch (e) {
      setError(errorText(e)); setStep('flags');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true); setError(null);
    try {
      const body = Object.fromEntries(Object.entries(edit).filter(([, v]) => v !== ''));
      if (kind === 'account') {
        const r = await inventoryApi.setCredential(row.id, body);
        setFlags(r.data);
      } else {
        await inventoryApi.setNetworkSecret(row.id, body);
        setFlags((f) => ({ ...f, stored: true }));
      }
      setEdit({}); setStep('flags');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const canReveal = flags && (isAdmin || flags.canReveal) && flags.stored;
  const noKey = flags && flags.keyConfigured === false;
  const title = kind === 'account' ? `Login of ${row?.label || ''}` : `Credentials of ${row?.label || ''}`;
  const storedList = kind === 'account' && flags
    ? [['password', flags.hasPassword], ['2FA', flags.hasTotp], ['backup codes', flags.hasBackupCodes],
      ['recovery email', flags.hasRecoveryEmail], ['recovery phone', flags.hasRecoveryPhone]].filter(([, on]) => on).map(([k]) => k)
    : flags?.stored ? ['username / password'] : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { clearTimeout(timer.current); setValues(null); } onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="inv-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> {title}</DialogTitle>
          <DialogDescription>Stored encrypted. Revealing is logged in History with your name and the time.</DialogDescription>
        </DialogHeader>
        {error && <p className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">{error}</p>}
        {noKey && (
          <p className="rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
            The server has no encryption key configured, so secrets cannot be stored or revealed.
          </p>
        )}
        {step === 'flags' && flags && (
          <div className="space-y-3 text-sm">
            <p>{storedList.length ? <>Stored: <b>{storedList.join(', ')}</b></> : 'Nothing stored yet.'}</p>
            <div className="flex flex-wrap gap-2">
              {canReveal && !noKey && (
                <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground" onClick={() => setStep('confirm')}>
                  Reveal…
                </button>
              )}
              {isAdmin && !noKey && (
                <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={() => setStep('edit')}>
                  {storedList.length ? 'Change…' : 'Add…'}
                </button>
              )}
            </div>
          </div>
        )}
        {step === 'confirm' && (
          <div className="space-y-3 text-sm" data-testid="inv-reveal-confirm">
            <p className="flex items-start gap-2"><ShieldAlert className="mt-0.5 h-4 w-4 text-warning" />
              This is logged in History. Reveal now?</p>
            <div className="flex gap-2">
              <button type="button" disabled={busy} className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground" onClick={reveal}>
                Yes, reveal
              </button>
              <button type="button" className="rounded-md border border-border px-3 py-1.5" onClick={() => setStep('flags')}>Cancel</button>
            </div>
          </div>
        )}
        {step === 'shown' && values && (
          <div className="space-y-2 text-sm" data-testid="inv-revealed">
            {Object.entries(values).filter(([, v]) => v != null && v !== '').map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-40 shrink-0 text-muted-foreground">{LABELS[k] || k}</span>
                <code className="min-w-0 flex-1 truncate rounded bg-secondary px-2 py-1" data-testid={`inv-secret-${k}`}>{v}</code>
                <button type="button" aria-label={`Copy ${LABELS[k] || k}`} title="Copy"
                  onClick={() => { try { navigator.clipboard?.writeText(v); } catch (e) { /* clipboard blocked */ } }}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground" onClick={() => { setValues(null); setStep('flags'); }}>
              <EyeOff className="h-3.5 w-3.5" /> Hide (auto in 30 s)
            </button>
          </div>
        )}
        {step === 'edit' && (
          <div className="space-y-2 text-sm" data-testid="inv-secret-edit">
            <p className="text-xs text-muted-foreground">Blank keeps the stored value.</p>
            {(fields || []).map((f) => (
              <label key={f} className="flex items-center gap-2">
                <span className="w-40 shrink-0 text-muted-foreground">{LABELS[f] || f}</span>
                <input type={/password|secret|codes/.test(f) ? 'password' : 'text'} autoComplete="off" value={edit[f] || ''}
                  onChange={(e) => setEdit({ ...edit, [f]: e.target.value })}
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1" aria-label={LABELS[f] || f} />
              </label>
            ))}
            <div className="flex gap-2 pt-1">
              <button type="button" disabled={busy} className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground" onClick={saveEdit}>Save encrypted</button>
              <button type="button" className="rounded-md border border-border px-3 py-1.5" onClick={() => { setEdit({}); setStep('flags'); }}>Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** CSV import: paste or pick a file, dry run first, then import. One bad line rejects the whole file (server rule). */
export function ImportDialog({ open, onOpenChange, table, importKey, onDone }) {
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCsv(''); setResult(null); setError(null); } }, [open]);

  const run = async (dryRun) => {
    setBusy(true); setError(null);
    try {
      const r = await inventoryApi.importCsv(table, csv, dryRun);
      setResult(r.data);
      if (!dryRun) onDone?.(r.data);
    } catch (e) {
      const body = e?.response?.data;
      if (body && Array.isArray(body.errors)) setResult(body);
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="inv-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Import {table} from CSV</DialogTitle>
          <DialogDescription>
            First row = column names. Rows match on {importKey?.length ? importKey.join(' + ') : 'their key'}: a match is updated, anything else is created.
            Empty cells keep the stored value. {table === 'accounts' && 'Client, network and groups go by name (groups separated by ";"); password / 2FA / recovery columns are stored encrypted.'}
          </DialogDescription>
        </DialogHeader>
        <input type="file" accept=".csv,text/csv" aria-label="CSV file" className="text-xs"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) f.text().then((t) => { setCsv(t); setResult(null); }).catch((err) => setError(String(err)));
          }} />
        <textarea value={csv} onChange={(e) => { setCsv(e.target.value); setResult(null); }} rows={8} aria-label="CSV text"
          placeholder={table === 'accounts' ? 'platform,username,status,client,groups,password\nINSTAGRAM,some_account,active,Client A,Batch Oct,...' : 'Paste CSV here'}
          className="w-full rounded-md border border-border bg-background p-2 font-mono text-xs" />
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        {result && (
          <div className="rounded-md border border-border p-2 text-xs" data-testid="inv-import-result">
            <p>
              {result.dryRun ? 'Dry run: ' : result.applied ? 'Imported: ' : 'Not imported: '}
              {result.created} new, {result.updated} updated, {result.unchanged} unchanged
              {result.groupsCreated?.length ? `, groups created: ${result.groupsCreated.join(', ')}` : ''}
              {result.ignoredColumns?.length ? ` (ignored columns: ${result.ignoredColumns.join(', ')})` : ''}
            </p>
            {result.errors?.length > 0 && (
              <ul className="mt-1 max-h-40 list-disc overflow-auto pl-5 text-danger">
                {result.errors.map((er) => <li key={er.line}>line {er.line}: {er.message}</li>)}
              </ul>
            )}
            {/* Re-review should-fix 5: rows that would leave a placement Assign refuses (imported all the same). */}
            {result.warnings?.length > 0 && (
              <ul className="mt-1 max-h-40 list-disc overflow-auto pl-5 text-warning" data-testid="inv-import-warnings">
                {result.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <button type="button" disabled={busy || !csv.trim()} className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40" onClick={() => run(true)}>
            Check (dry run)
          </button>
          <button type="button" disabled={busy || !result || !result.dryRun || result.errors?.length > 0}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-40" onClick={() => run(false)}>
            Import {result?.dryRun ? (result.created + result.updated) : ''} rows
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Generic "add a row" form from the server's field metadata (writable, non-launcher fields). */
export function CreateDialog({ open, onOpenChange, table, fields, columns, onCreated }) {
  const [values, setValues] = useState({});
  const [chips, setChips] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setValues({}); setChips({}); setError(null); } }, [open]);
  const editable = (fields || []).filter((f) => f.writable && !f.auto && f.kind !== 'LONGTEXT' && f.name !== 'igaccId');

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const body = {};
      for (const [k, v] of Object.entries(values)) if (v !== '' && v != null) body[k] = v;
      for (const [k, c] of Object.entries(chips)) if (c) body[k] = c.id;
      const r = await inventoryApi.create(table, body);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="inv-create-dialog">
        <DialogHeader>
          <DialogTitle>Add to {table}</DialogTitle>
          <DialogDescription>Required fields are marked *. Everything else can be filled in later in the table.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {editable.map((f) => (
            <label key={f.name} className="flex items-center gap-2">
              {/* INV-7b: the column's label ("Client"), not the API field name ("clientId"). */}
              <span className="w-36 shrink-0 text-muted-foreground">{fieldLabel(columns, f.name)}{f.required ? ' *' : ''}</span>
              {f.kind === 'ENUM' ? (
                <select aria-label={f.name} value={values[f.name] || ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1">
                  <option value="">—</option>
                  {f.values.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : f.kind === 'REF' ? (
                chips[f.name] ? <Chip chip={chips[f.name]} onClick={() => setChips({ ...chips, [f.name]: null })} />
                  : <div className="min-w-0 flex-1"><LinkPicker refTable={f.ref} autoFocus={false} onPick={(c) => setChips({ ...chips, [f.name]: c })} /></div>
              ) : f.kind === 'BOOL' ? (
                <input type="checkbox" aria-label={f.name} checked={!!values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.checked })} />
              ) : (
                <input aria-label={f.name} type={f.kind === 'DATE' ? 'date' : f.kind === 'INT' || f.kind === 'DOUBLE' ? 'number' : 'text'}
                  value={values[f.name] || ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1" />
              )}
            </label>
          ))}
        </div>
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        <div className="flex gap-2 pt-2">
          <button type="button" disabled={busy} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground" onClick={submit}>Create</button>
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={() => onOpenChange(false)}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
